/*
 * doi-cart-offers.js — free tote + parcel protection cart offers
 * ---------------------------------------------------------------------------
 * Replaces the script half of snippets/cart-tote-card.liquid, which caused the
 * 2026-04-22 /cart.js rate-limit cascade (~14.5h, ~80% mobile CVR drop).
 *
 * The old failure loop was:
 *   DOM mutation -> MutationObserver -> debouncedSetup -> fetchCart -> addTote
 *   -> cart re-renders -> DOM mutation -> ...
 * plus fetchCart() calling res.json() on a 429, which returns {items: []} — an
 * apparently EMPTY cart — which then triggered removeTote(), which fetched the
 * cart AGAIN. A 429 directly generated more requests. That is what turned a
 * slow cart into a dead one.
 *
 * HARD INVARIANTS. Every one of these is load-bearing; do not relax one
 * without re-running scripts/verify-mobile.mjs including the 429 injection.
 *
 *   1. This module NEVER reads /cart.js. Cart state comes from Liquid at
 *      render time (data-* on the card) or from an event's detail. Zero reads.
 *   2. No MutationObserver, anywhere. Listeners are delegated on `document`
 *      and bound exactly once, so section re-renders never rebind or stack.
 *   3. Every write goes through writeCart(): one serial queue, a hard minimum
 *      gap between writes, explicit res.ok checks, and .json() only on a 2xx.
 *   4. Writes are only ever caused by (a) a real user gesture, or (b) an
 *      automatic tote add, capped at MAX_AUTOADD_ATTEMPTS per session. The
 *      counter is incremented BEFORE the request, so a failure can never buy
 *      extra attempts and a feedback loop burns through the ceiling and stops.
 *      (It is a small ceiling rather than exactly one because navigating away
 *      mid-request aborts the fetch — "add to cart, then tap checkout" is
 *      ordinary — and a hard one-shot silently lost the gift for the session.)
 *   5. Two consecutive failures trip a circuit breaker: the offers hide, the
 *      module stops for the whole session, and the customer's cart is left
 *      exactly as the theme made it. Failure is always silent and inert.
 *
 * Consequence: the worst case for a session is MAX_AUTOADD_ATTEMPTS automatic
 * writes plus one write per deliberate click, each at least MIN_WRITE_GAP_MS
 * apart, with the breaker tripping long before the ceiling is reached in any
 * genuine failure. There is no code path where a response causes a request.
 */
(function () {
  'use strict';

  if (window.DOI_CartOffers) return;

  var MIN_WRITE_GAP_MS = 1200; // floor between any two cart writes
  var BACKOFF_MS = [2000, 4000]; // 429/5xx retry ladder, then trip
  var MAX_CONSECUTIVE_FAILURES = 2;
  var MAX_AUTOADD_ATTEMPTS = 3;   // ceiling on automatic tote adds per session

  var KILL_KEY = 'doi-offers-off'; // sessionStorage: breaker tripped
  var AUTOADD_KEY = 'doi-tote-autoadd'; // sessionStorage: auto-add already attempted
  var ADDED_KEY = 'doi-tote-added';     // sessionStorage: an add already SUCCEEDED
  var CLEANUP_KEY = 'doi-tote-cleanup'; // sessionStorage: orphan removal already attempted
  var OPTOUT_KEY = 'doi-tote-optout'; // localStorage: customer said no thanks

  // --- storage that never throws (Safari private mode, ITP, etc.) -----------

  function readStore(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function writeStore(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) {}
  }
  function dropStore(store, key) {
    try { window[store].removeItem(key); } catch (e) {}
  }

  // --- circuit breaker -----------------------------------------------------

  var failures = 0;
  var tripped = readStore('sessionStorage', KILL_KEY) === '1';

  function trip(reason) {
    if (tripped) return;
    tripped = true;
    writeStore('sessionStorage', KILL_KEY, '1');
    document.querySelectorAll('[data-offer]').forEach(function (el) {
      // Never hide the control for a CHARGE that is in the cart. The fee line
      // is deliberately left out of the product list, so hiding its tickbox
      // would leave an invisible, unremovable A$4.50 in the cart. Keep it on
      // screen, ticked — honest about what the customer is paying for.
      if (el.getAttribute('data-offer') === 'protection' && el.getAttribute('data-in-cart') === 'true') return;
      el.hidden = true;
    });
    if (window.console && console.warn) {
      console.warn('[cart-offers] disabled for this session:', reason);
    }
  }

  // --- write queue ---------------------------------------------------------
  //
  // Serial by construction: every write appends to one promise chain, so two
  // writes can never be in flight together no matter how fast the customer
  // clicks. lastWriteAt is stamped before each attempt (including retries), so
  // a backoff can never collapse the gap.

  // True once an automatic/claimed add has succeeded on this page load. The
  // DOM's data-in-cart lags behind the theme's async section re-render, so it
  // cannot be trusted as the guard against a duplicate add.
  var toteAdded = false;

  var chain = Promise.resolve();
  var lastWriteAt = 0;

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  var pendingWrites = 0;
  function enqueue(task, force) {
    pendingWrites++;
    chain = chain.then(function () {
      if (tripped && !force) return null;
      var wait = MIN_WRITE_GAP_MS - (Date.now() - lastWriteAt);
      return (wait > 0 ? sleep(wait) : Promise.resolve()).then(task);
    }).catch(function (err) {
      // Swallow. A rejected write must never re-run and must never surface to
      // the customer — the theme's own cart still works.
      noteFailure();
      if (window.console && console.warn) console.warn('[cart-offers]', err && err.message);
      return null;
    }).then(function (v) { pendingWrites--; return v; });
    return chain;
  }

  function noteFailure() {
    failures += 1;
    if (failures >= MAX_CONSECUTIVE_FAILURES) trip('repeated cart write failures');
  }

  /*
   * The only place this module talks to Shopify.
   * Returns the parsed body on success, or null on any failure.
   * Never calls .json() on a non-2xx response — that is what produced the
   * phantom empty cart in April.
   */
  async function writeCart(path, body, attempt) {
    attempt = attempt || 0;
    lastWriteAt = Date.now();

    var res;
    try {
      res = await fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    } catch (e) {
      noteFailure();
      return null; // offline / aborted — do not retry
    }

    if (res.status === 429 || res.status >= 500) {
      if (attempt >= BACKOFF_MS.length) {
        trip('rate limited (' + res.status + ')');
        return null;
      }
      await sleep(BACKOFF_MS[attempt]);
      return writeCart(path, body, attempt + 1);
    }

    if (!res.ok) {
      // 422 = variant unavailable / product not published / out of stock.
      // Deliberately NOT retried: retrying a 422 is an infinite loop.
      noteFailure();
      return null;
    }

    failures = 0;
    var payload;
    try {
      payload = await res.json();
    } catch (e) {
      noteFailure();
      return null;
    }
    injectBundled(payload);
    return payload;
  }

  /*
   * Bundled section rendering — the theme's own mechanism (cart-api.js
   * dispatchBundledSection). A write that asks for `sections: 'cart-contents'`
   * gets the re-rendered drawer HTML back in the same response; handing it to
   * the theme via `cart:section-html` swaps it in with no extra request.
   *
   * Needed because the theme's onCartFinished handler SKIPS its own refresh for
   * 3 s after any bundled injection (sections.cart.js: `bundled = Date.now() -
   * lastBundledInjection < 3000`). A default-on add right after a PDP
   * add-to-cart lands inside that window, so without this the drawer kept the
   * pre-fee render: box ticked, subtotal without the fee (screenshot 22-Oct).
   */
  var SECTION = 'cart-contents';
  function withSections(body) {
    body.sections = SECTION;
    return body;
  }
  function injectBundled(payload) {
    if (!payload || !payload.sections) return;
    var html = payload.sections[SECTION];
    delete payload.sections;
    if (!html) return;
    try {
      document.dispatchEvent(new CustomEvent('cart:section-html', { detail: { html: html } }));
    } catch (e) {}
  }

  // --- telling the theme the cart changed ----------------------------------
  //
  // The theme listens on `document` for onCartFinished and re-renders the cart
  // section itself. This module deliberately does NOT listen to that event for
  // anything except the once-per-session auto-add check, so dispatching it
  // cannot come back around.

  function notifyTheme(cart) {
    try {
      if (cart) {
        document.dispatchEvent(new CustomEvent('onCartUpdated', { detail: cart }));
      }
      document.dispatchEvent(new CustomEvent('onCartFinished', { detail: cart || undefined }));
    } catch (e) {}
  }

  // --- measurement ---------------------------------------------------------
  //
  // Restored from the April version, which reported every include/decline to
  // the dashboard (tote_events / tote_config, surfaced at /api/tote/stats).
  // Without it there is no attach rate and no opt-out rate, and the trial
  // cannot be judged — so it goes back in.
  //
  // sendBeacon only: fire-and-forget, no response, not part of the write queue,
  // and it cannot contribute to a cart rate limit because it never touches a
  // /cart endpoint.

  function sessionId() {
    var sid = readStore('sessionStorage', 'doi-sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2);
      writeStore('sessionStorage', 'doi-sid', sid);
    }
    return sid;
  }

  // 50/50 experiment arm (custom.protection_mode = "experiment"), set on <html>
  // by the <head> script in theme.liquid from cookie doi_prot_arm.
  function protArm() {
    var c = document.documentElement.classList;
    return c.contains('doi-prot-on') ? 'on' : c.contains('doi-prot-off') ? 'off' : '';
  }
  // Promise-line 50/50 (custom.promise_mode = "experiment"), cookie doi_promise_arm.
  function promiseArm() {
    var c = document.documentElement.classList;
    return c.contains('doi-promise-on') ? 'on' : c.contains('doi-promise-off') ? 'off' : '';
  }

  function beacon(el, action) {
    try {
      var url = el && el.getAttribute('data-beacon');
      if (!url || !navigator.sendBeacon) return;
      var body = JSON.stringify({
        action: action,
        offer: el.getAttribute('data-offer') || '',
        mode: el.getAttribute('data-tote-mode') ||
          (el.getAttribute('data-experiment') === 'true' ? 'exp_' + protArm() + '_' : '') + (el.getAttribute('data-mode') || ''),
        country: el.getAttribute('data-country') || '',
        sid: sessionId(),
        ts: Date.now()
      });
      navigator.sendBeacon(url + '/api/tote/event', new Blob([body], { type: 'application/json' }));
    } catch (e) {}
  }

  // --- where is this customer actually going to ship to? --------------------
  //
  // The Liquid market gate answers "which SITE is this?", not "where does this
  // person live?". 3% of US-address orders in the last 30 days were placed in
  // AUD on the www site — visitors who clicked "stay" on the geo prompt. Every
  // one of them would have received an AU-gated tote LINE, which at J&J is
  // billed as a product pick and may not even have product stock to pick from.
  //
  // The geo-redirect already knows their detected country and keeps it in a
  // cross-subdomain cookie for 30 days: _doi_geo = "stay:US" or "market:US".
  // Reading it here closes most of that gap without a network call. The true
  // fix — acting on the shipping address at checkout — needs a checkout
  // extension; this is the belt to that brace.

  function detectedCountry() {
    try {
      var m = document.cookie.match(/(?:^|; )_doi_geo=([^;]*)/);
      if (!m) return null;
      var v = decodeURIComponent(m[1]);
      var parts = v.split(':');
      var cc = (parts.length > 1 ? parts[1] : parts[0]).toUpperCase();
      return /^[A-Z]{2}$/.test(cc) ? cc : null;
    } catch (e) { return null; }
  }

  function outsideMarkets(el) {
    var allowed = ((el && el.getAttribute('data-markets')) || '').toUpperCase().split(',');
    var cc = detectedCountry();
    return !!(cc && allowed.length && allowed.indexOf(cc) === -1);
  }

  // --- card helpers --------------------------------------------------------

  function card(name) {
    return document.querySelector('[data-offer="' + name + '"]');
  }

  function intAttr(el, name) {
    var v = parseInt(el && el.getAttribute(name), 10);
    return isNaN(v) ? 0 : v;
  }

  function setState(el, state) {
    if (!el) return;
    el.setAttribute('data-offer-state', state);
  }

  // --- tote ----------------------------------------------------------------
  //
  // Two trial modes, chosen by data-tote-mode on the card (and overridable
  // with ?tote_mode=claim|gift for side-by-side testing on a preview theme):
  //
  //   claim — nothing is added until the customer asks for it. One write, on
  //           a real click. Nothing ever appears in the cart unbidden.
  //   gift  — added automatically once per session, removable via "no thanks".
  //           This is the April behaviour, but with the loop removed: it can
  //           fire at most once per session, from server-rendered state, and
  //           the flag is set before the request.

  function toteMode(el) {
    var override = null;
    try { override = new URLSearchParams(location.search).get('tote_mode'); } catch (e) {}
    var mode = override || (el && el.getAttribute('data-tote-mode')) || 'claim';
    return mode === 'gift' ? 'gift' : 'claim';
  }

  /*
   * The cart card shows the strike-through value ourselves, in Liquid. Shopify's
   * checkout does not: it only renders a strike-through that a DISCOUNT
   * produced, so a /usr/bin/bash line with a 0 compare-at appears as plain "/usr/bin/bash.00" — in
   * checkout, on the confirmation email and on the order status page. The value
   * story is strongest in the cart and then vanishes at the moment of payment.
   *
   * Line-item properties WITHOUT a leading underscore are displayed under the
   * line in all three places. So the value is carried as a visible property,
   * quoted in the presentment currency Liquid rendered. The underscored ones
   * stay hidden and remain the identifiers the pack desk and reporting use.
   */
  function toteProperties(el) {
    var value = (el && el.getAttribute('data-value')) || '';
    var props = { _tote: 'true', _gift: 'true' };
    if (value) props['Gift'] = 'Free with your order — ' + value + ' value';
    return props;
  }

  function addTote(el) {
    var variant = intAttr(el, 'data-variant-id');
    if (!variant) return Promise.resolve(null);
    // Only a customer who previously declined can have a "do not pack" note to
    // clear. Checking first keeps the ordinary add at exactly one write.
    var wasDeclined = el.getAttribute('data-offer-state') === 'declined';
    setState(el, 'working');
    return enqueue(function () {
      return writeCart('/cart/add.js', {
        items: [{ id: variant, quantity: 1, properties: toteProperties(el) }]
      }).then(function (result) {
        if (!result) { setState(el, 'idle'); return null; }
        /*
         * Record the success in memory AND on the element before telling the
         * theme. notifyTheme dispatches onCartFinished, which this module also
         * listens to, and the theme's section re-render is asynchronous — so
         * the very next sync() would otherwise read a stale
         * data-in-cart="false" and add a SECOND tote. Observed: quantity 2.
         *
         * The attempt ceiling bounded that to two rather than a cascade, but
         * bounded-and-wrong is still wrong. In-memory state is the authority
         * for the rest of this page load.
         */
        toteAdded = true;
        writeStore('sessionStorage', ADDED_KEY, '1');
        if (wasDeclined) clearDeclineNote();
        el.setAttribute('data-in-cart', 'true');
        setState(el, 'in-cart');
        beacon(el, 'include');

        /*
         * Self-heal a duplicate rather than hoping one never happens.
         *
         * /cart/add.js INCREMENTS, and the guards above cannot close every
         * race: if the customer navigates immediately after an add, the request
         * still reaches Shopify but this page never sees the response, so the
         * next page load has no record of success and adds again. Observed as
         * "Daughters of India Tote Bag x2".
         *
         * The add response reports the resulting line quantity, so a duplicate
         * is detectable from what we already have — no /cart.js read. One
         * corrective write, from a response we already hold, and it converges.
         */
        var line = result && result.items && result.items[0];
        if (line && line.quantity > 1 && line.key) {
          enqueue(function () {
            return writeCart('/cart/change.js', { id: line.key, quantity: 1 }).then(function (cart) {
              if (cart) notifyTheme(cart);
              return cart;
            });
          });
          return result;
        }

        notifyTheme(null);
        return result;
      });
    });
  }

  /*
   * Declining does TWO things in ONE request: it takes the line out, and it
   * stamps an explicit note on the cart.
   *
   * The pack desk's rule is "include a tote if the order has one, otherwise
   * don't". That flips their default from always-pack to only-pack-when-
   * flagged, which means absence of the line now carries meaning — and absence
   * is exactly what a technical failure also looks like. A tripped breaker, an
   * ad-blocker, a checkout reached before the add landed, or an order raised as
   * a draft all produce a tote-less order that nobody chose.
   *
   * So the decline is stated, not inferred:
   *     line present                 -> pack one
   *     no line + this attribute     -> the customer said no, don't pack
   *     no line + no attribute       -> the script never ran, pack one as before
   *
   * A failure then degrades to today's behaviour instead of quietly cancelling
   * a gift. /cart/update.js takes `updates` and `attributes` together, so this
   * still costs exactly one write.
   */
  /* Someone who declines and then changes their mind must not leave a stale
   * 'do not pack' note behind the tote line they just restored. */
  function clearDeclineNote() {
    enqueue(function () {
      return writeCart('/cart/update.js', { attributes: { Tote: '' } });
    });
  }

  function removeTote(el, reason) {
    var key = el && el.getAttribute('data-line-key');
    var variantId = intAttr(el, 'data-variant-id');
    if (!key || !variantId) return Promise.resolve(null);
    setState(el, 'working');
    return enqueue(function () {
      var body = { updates: {}, attributes: {} };
      body.updates[variantId] = 0;
      body.attributes['Tote'] = reason === 'cleanup'
        ? ''
        : 'Declined by customer — do not pack';
      // /cart/update.js returns the full cart, so the theme gets real numbers
      // back without this module ever reading /cart.js.
      return writeCart('/cart/update.js', body).then(function (cart) {
        if (!cart) { setState(el, 'idle'); return null; }
        toteAdded = false;
        dropStore('sessionStorage', ADDED_KEY);
        el.setAttribute('data-in-cart', 'false');
        setState(el, reason === 'cleanup' ? 'idle' : 'declined');
        beacon(el, reason || 'optout');
        notifyTheme(cart);
        return cart;
      });
    });
  }

  /*
   * The customer emptied their cart of everything they actually chose, and our
   * free tote is all that is left. Take it back out.
   *
   * The April version did this and it was dropped in the rewrite; the result
   * was a cart that rendered as empty, offered no checkout button, and still
   * carried a $0 line in cart.item_count. Liquid renders the card hidden in
   * this state purely so this can run.
   *
   * Guarded once per session like the auto-add, for the same reason: a failure
   * must not be able to re-arm it.
   */
  function maybeCleanupTote() {
    if (tripped) return;
    var el = card('tote');
    if (!el || el.getAttribute('data-offer-state') !== 'cleanup') return;
    if (readStore('sessionStorage', CLEANUP_KEY)) return;
    writeStore('sessionStorage', CLEANUP_KEY, '1'); // before the request
    removeTote(el, 'cleanup');
  }

  /*
   * Restore the declined state on later visits, so someone who already said
   * "I already have one" is not silently re-asked — and still sees the thank
   * you. The April card did this via a persisted class; the state now derives
   * from the stored preference plus what Liquid says is in the cart.
   */
  function restoreDeclined() {
    var el = card('tote');
    if (!el) return;
    if (el.getAttribute('data-in-cart') === 'true') return;
    if (el.getAttribute('data-offer-state') === 'cleanup') return;
    if (readStore('localStorage', OPTOUT_KEY) === '1') setState(el, 'declined');
  }

  /*
   * A BOUNDED number of automatic adds per session — not one, and not
   * unlimited.
   *
   * The first version allowed exactly one and wrote the flag before the
   * request, so nothing could re-arm it. That is cascade-proof but it loses
   * the gift outright whenever the request never completes: navigating away
   * mid-flight aborts the fetch, and "add to cart then immediately tap
   * checkout" is a completely ordinary thing to do. Observed in testing —
   * the flag was set, the write never happened, and the customer silently got
   * no tote for the rest of the session.
   *
   * A counter incremented BEFORE each request keeps the cascade guarantee —
   * the ceiling is fixed and monotonic, so a feedback loop burns through it
   * and stops — while surviving an ordinary interrupted page.
   *
   * This cannot help a customer who reaches checkout before the add lands;
   * that is inherent to adding from the cart, and is a real cost of gift mode.
   */
  function maybeAutoAddTote(cart) {
    if (tripped) return;
    if (toteAdded) return;                                  // this page load
    if (readStore('sessionStorage', ADDED_KEY) === '1') return; // an earlier one
    if (Number(readStore('sessionStorage', AUTOADD_KEY) || 0) >= MAX_AUTOADD_ATTEMPTS) return;
    if (readStore('localStorage', OPTOUT_KEY) === '1') return;

    var el = card('tote');
    if (!el || toteMode(el) !== 'gift') return;
    if (outsideMarkets(el)) { el.hidden = true; return; }
    if (el.getAttribute('data-in-cart') === 'true') return;

    // Eligibility: at least one non-tote item. Prefer the event's cart when
    // the theme handed us one; otherwise trust what Liquid rendered.
    var eligible;
    if (cart && cart.items) {
      eligible = cart.items.some(function (i) {
        return !(i.properties && i.properties._tote === 'true');
      });
    } else {
      eligible = el.getAttribute('data-eligible') === 'true';
    }
    if (!eligible) return;

    // Incremented before the request, never after, so a failure cannot buy
    // extra attempts and a loop is capped at MAX_AUTOADD_ATTEMPTS writes.
    var n = Number(readStore('sessionStorage', AUTOADD_KEY) || 0);
    writeStore('sessionStorage', AUTOADD_KEY, String(n + 1));
    addTote(el);
  }

  // --- parcel protection ---------------------------------------------------
  //
  // A tickbox, opt-in only, never pre-ticked: the box's `checked` comes from
  // Liquid, i.e. from whether the fee line is really in the cart. A pre-ticked
  // one-time fee can be a "negative option" (Harris v. LoveShackFancy, 2026)
  // and is a named dark pattern here. One flat price, one variant.
  //
  // Removal goes by VARIANT id through /cart/update.js, not by line key. Right
  // after an add the card still carries the pre-add markup with no line key
  // (the theme's re-render is async), so a quick tick-untick would otherwise
  // find nothing to remove — the box would show unticked while the fee stayed
  // in the cart. Setting the variant's quantity to 0 needs no key.

  function toggleBox(el, on) {
    var box = el && el.querySelector('[data-offer-toggle]');
    if (box) box.checked = !!on;
  }

  var OPTOUT_ATTR = '_prot_optout'; // cart attribute: customer unticked it
  var PROT_AUTO_KEY = 'doi-prot-autoadd'; // sessionStorage: automatic adds tried
  var protAdded = false; // an add succeeded on this page load
  var protAdding = false; // an add is queued or in flight
  var protCancel = false; // customer unticked while that add was in flight

  function addProtection(el) {
    var variant = intAttr(el, 'data-variant-id');
    if (!variant) return Promise.resolve(null);
    setState(el, 'working');
    protAdding = true;
    // Re-ticking after a decline: clear the decline first, so the next render
    // (and the order) no longer says "declined".
    if (el.getAttribute('data-optout') === 'true') {
      var clear = { attributes: {} }; clear.attributes[OPTOUT_ATTR] = '';
      enqueue(function () { return writeCart('/cart/update.js', clear); });
      el.setAttribute('data-optout', 'false');
    }
    return enqueue(function () {
      return writeCart('/cart/add.js', withSections({
        items: [{ id: variant, quantity: 1, properties: { _protection: 'true' } }]
      })).then(function (result) {
        // Failure: the customer must never be left looking at a ticked box
        // for a fee that isn't in the cart.
        protAdding = false;
        if (!result) { setState(el, 'idle'); toggleBox(el, false); return null; }
        protAdded = true;
        el.setAttribute('data-in-cart', 'true');
        if (protCancel) {
          protCancel = false;
          beacon(el, 'protect_auto');
          removeProtection(el);
          return result;
        }
        setState(el, 'in-cart');
        toggleBox(el, true);
        beacon(el, el.getAttribute('data-pending') === 'true' ? 'protect_auto' : 'protect_add');
        el.setAttribute('data-pending', 'false');

        // /cart/add.js INCREMENTS. Same self-heal as the tote: if a race left
        // two, correct to one from the response we already hold.
        var line = result && result.items && result.items[0];
        if (line && line.quantity > 1) {
          var fix = withSections({ updates: {} }); fix.updates[variant] = 1;
          enqueue(function () {
            return writeCart('/cart/update.js', fix).then(function (cart) {
              if (cart) notifyTheme(cart);
              return cart;
            });
          });
          return result;
        }
        notifyTheme(null);
        return result;
      });
    });
  }

  function removeProtection(el, reason) {
    var variant = intAttr(el, 'data-variant-id');
    if (!variant) return Promise.resolve(null);
    setState(el, 'working');
    return enqueue(function () {
      var body = withSections({ updates: {} }); body.updates[variant] = 0;
      // A customer's untick is remembered on the cart itself (same request),
      // so default-on never re-adds it, and the order records the decline.
      // Cleanup of an orphan fee is not a decline.
      if (reason !== 'cleanup') { body.attributes = {}; body.attributes[OPTOUT_ATTR] = '1'; }
      return writeCart('/cart/update.js', body).then(function (cart) {
        // Failure: leave the box ticked, because the fee is still there.
        if (!cart) { setState(el, 'in-cart'); toggleBox(el, true); return null; }
        protAdded = false;
        el.setAttribute('data-pending', 'false');
        if (reason !== 'cleanup') el.setAttribute('data-optout', 'true');
        el.setAttribute('data-in-cart', 'false');
        setState(el, 'idle');
        beacon(el, reason === 'cleanup' ? 'protect_cleanup' : 'protect_remove');
        notifyTheme(cart);
        return cart;
      });
    }, true);
  }

  /*
   * The customer removed everything of their own and the protection fee is
   * all that's left — a real charge in a cart that renders as empty. Liquid
   * emits a hidden card in state "cleanup" only in that case. Once per session,
   * flag written before the request, like the tote cleanup.
   */
  var PROT_CLEANUP_KEY = 'doi-prot-cleanup';
  function maybeCleanupProtection() {
    if (tripped) return;
    var el = card('protection');
    if (!el || el.getAttribute('data-offer-state') !== 'cleanup') return;
    // A counter, not a one-shot: with default-on, a shopper can empty the bag,
    // refill it (fee auto-added again) and empty it again in one session.
    var n = Number(readStore('sessionStorage', PROT_CLEANUP_KEY) || 0);
    if (n >= MAX_AUTOADD_ATTEMPTS) return;
    writeStore('sessionStorage', PROT_CLEANUP_KEY, String(n + 1)); // before the request
    removeProtection(el, 'cleanup');
  }

  /*
   * Default-on mode (theme setting doi_protection_mode = "default_on"). Liquid
   * renders the box ticked with data-pending="true" when the fee is not yet in
   * the cart and the customer hasn't declined (cart attribute). Here we make
   * that true by adding the fee, so the box, the total and checkout agree.
   * Bounded like the tote: the attempt counter is written before the request,
   * so a failure or a loop can cost at most MAX_AUTOADD_ATTEMPTS writes per
   * session. If we won't add, the box is unticked — never ticked over nothing.
   */
  var ARM_KEY = 'doi-prot-armstamp';
  function maybeStampArm() {
    var el = card('protection');
    if (!el || el.getAttribute('data-experiment') !== 'true') return;
    var arm = protArm();
    if (!arm || readStore('sessionStorage', ARM_KEY) === arm) return;
    writeStore('sessionStorage', ARM_KEY, arm); // before the request
    enqueue(function () { return writeCart('/cart/update.js', { attributes: { __doi_prot_arm: arm } }); });
  }
  // Same for the promise line: the bag (and so the order) records its group.
  // The card renders in both arms (the off arm hides it), so both get stamped.
  var PROMISE_ARM_KEY = 'doi-promise-armstamp';
  function maybeStampPromiseArm() {
    var el = card('promise');
    if (!el || el.getAttribute('data-experiment') !== 'true') return;
    var arm = promiseArm();
    if (!arm || readStore('sessionStorage', PROMISE_ARM_KEY) === arm) return;
    writeStore('sessionStorage', PROMISE_ARM_KEY, arm); // before the request
    enqueue(function () { return writeCart('/cart/update.js', { attributes: { __doi_promise_arm: arm } }); });
  }
  function phRegister() {
    var props = {}, any = false;
    if (protArm()) { props.prot_arm = protArm(); any = true; }
    if (promiseArm()) { props.promise_arm = promiseArm(); any = true; }
    if (!any || !window.posthog || !window.posthog.register) return false;
    try { window.posthog.register(props); } catch (e) {}
    return true;
  }
  if (!phRegister()) setTimeout(phRegister, 4000);

  var PROT_QTY_KEY = 'doi-prot-qtyfix';
  function maybeFixProtectionQty() {
    var el = card('protection');
    if (!el || intAttr(el, 'data-qty') <= 1) return;
    var n = Number(readStore('sessionStorage', PROT_QTY_KEY) || 0);
    if (n >= MAX_AUTOADD_ATTEMPTS) return;
    writeStore('sessionStorage', PROT_QTY_KEY, String(n + 1));
    var variant = intAttr(el, 'data-variant-id');
    var fix = withSections({ updates: {} }); fix.updates[variant] = 1;
    el.setAttribute('data-qty', '1');
    enqueue(function () { return writeCart('/cart/update.js', fix); }, true);
  }

  function maybeAutoAddProtection(cart) {
    var el = card('protection');
    if (!el || el.getAttribute('data-pending') !== 'true') return;
    if (el.getAttribute('data-in-cart') === 'true') return;
    if (protAdding) return; // a re-render while our add is in flight
    if (el.getAttribute('data-experiment') === 'true' && protArm() !== 'on') {
      el.setAttribute('data-pending', 'false'); toggleBox(el, false); return;
    }
    var n = Number(readStore('sessionStorage', PROT_AUTO_KEY) || 0);
    if (tripped || protAdded || n >= MAX_AUTOADD_ATTEMPTS) {
      el.setAttribute('data-pending', 'false'); toggleBox(el, false); return;
    }
    if (cart && cart.items) {
      var fee = cart.items.some(function (i) { return i.properties && i.properties._protection === 'true'; });
      if (fee) return;
      var real = cart.items.some(function (i) {
        return i.requires_shipping && !(i.properties && (i.properties._protection === 'true' || i.properties._tote === 'true'));
      });
      if (!real) return;
      if (cart.attributes && cart.attributes[OPTOUT_ATTR] === '1') {
        el.setAttribute('data-pending', 'false'); toggleBox(el, false); return;
      }
    }
    writeStore('sessionStorage', PROT_AUTO_KEY, String(n + 1));
    addProtection(el);
  }

  // The tickbox. `change`, not `click`, so keyboard and label taps behave.
  function onChange(e) {
    var box = e.target;
    if (!box || !box.matches || !box.matches('[data-offer-toggle]')) return;
    var el = box.closest('[data-offer="protection"]');
    if (!el) return;
    // Unticking a fee that is (or is about to be) in the cart is always
    // allowed — even after the breaker trips — so a charge can never be locked
    // in. Ticking is what the breaker and the "working" state hold back.
    if (!box.checked && protAdding) { protCancel = true; return; }
    if (!box.checked && el.getAttribute('data-in-cart') === 'true') { removeProtection(el); return; }
    if (tripped) { box.checked = !box.checked; return; }
    if (el.getAttribute('data-offer-state') === 'working') { box.checked = !box.checked; return; }
    // No geo-cookie gate here. Protection is gated by MARKET in Liquid (the
    // store the customer is buying from, in its currency) — that is the only
    // gate. The geo cookie was built for the tote, where a US shopper on the AU
    // site would create a physical line J&J can't fulfil; for a fee it is just
    // a guess at location, and a wrong one for Greg's own connection (reads NZ).
    // With it, the card arrived via the theme's section injection un-gated,
    // then vanished the moment it was ticked. Found by Greg, 28-Sep.
    if (box.checked) addProtection(el);
    else removeProtection(el);
  }
  document.addEventListener('change', onChange);

  // --- delegated events ----------------------------------------------------
  //
  // Bound once on `document`. Section re-renders replace the card markup
  // freely; nothing needs rebinding and no handler can ever stack. This is
  // also the fix for the opt-out toggle that would not roll back up — the old
  // code reassigned toggle.onchange on every setup() pass and kept its state
  // in three places (localStorage, DOM classes, the cart) that drifted apart.

  function onClick(e) {
    if (tripped) return;

    var claim = e.target.closest && e.target.closest('[data-offer-claim]');
    if (claim) {
      var claimCard = claim.closest('[data-offer]');
      if (!claimCard || claimCard.getAttribute('data-offer-state') === 'working') return;
      e.preventDefault();
      if (claimCard.getAttribute('data-offer') === 'tote') {
        if (outsideMarkets(claimCard)) { claimCard.hidden = true; return; }
        dropStore('localStorage', OPTOUT_KEY);
        // An explicit claim retires the automatic path for the session.
        writeStore('sessionStorage', AUTOADD_KEY, String(MAX_AUTOADD_ATTEMPTS));
        addTote(claimCard);
      } else {
        addProtection(claimCard);
      }
      return;
    }

    var decline = e.target.closest && e.target.closest('[data-offer-decline]');
    if (decline) {
      var declineCard = decline.closest('[data-offer]');
      if (!declineCard || declineCard.getAttribute('data-offer-state') === 'working') return;
      e.preventDefault();
      if (declineCard.getAttribute('data-offer') === 'tote') {
        writeStore('localStorage', OPTOUT_KEY, '1');
        removeTote(declineCard);
      } else {
        removeProtection(declineCard);
      }
    }
  }

  document.addEventListener('click', onClick);

  // The ONLY cart-event listener in this module, and it can do at most one
  // thing once per session. Guarded before it can reach a request.
  /*
   * ?tote_reset=1 forgets everything this browser remembers: the opt-out, the
   * auto-add counter, the success flag and a tripped breaker.
   *
   * The opt-out is deliberately permanent — "I already have one" stays true —
   * so once anyone has declined, they see the declined card forever and can
   * never observe the gift behaviour again without clearing site data. That
   * makes the feature genuinely hard to demo or QA. This is the escape hatch.
   */
  function maybeReset() {
    try {
      if (!new URLSearchParams(location.search).has('tote_reset')) return;
    } catch (e) { return; }
    dropStore('localStorage', OPTOUT_KEY);
    dropStore('sessionStorage', AUTOADD_KEY);
    dropStore('sessionStorage', ADDED_KEY);
    dropStore('sessionStorage', CLEANUP_KEY);
    dropStore('sessionStorage', PROT_CLEANUP_KEY);
    dropStore('sessionStorage', PROT_AUTO_KEY);
    dropStore('sessionStorage', ARM_KEY);
    dropStore('sessionStorage', KILL_KEY);
    tripped = false;
    failures = 0;
    toteAdded = false;
    if (window.console) console.log('[cart-offers] state reset');
  }

  // Each offer is gated and handled independently. The first version returned
  // early from the tote's market check, which would have silently skipped
  // everything after it — harmless while the tote was the only offer, wrong
  // the moment a second one exists.
  function sync(cart) {
    // Protection: market-gated in Liquid only (see onChange). No geo hide.
    maybeCleanupProtection();
    maybeFixProtectionQty();
    maybeAutoAddProtection(cart);
    maybeStampArm();
    maybeStampPromiseArm();
    fixHeaderCount(cart);

    var t = card('tote');
    if (t && outsideMarkets(t)) { t.hidden = true; return; }
    restoreDeclined();
    maybeCleanupTote();
    maybeAutoAddTote(cart);
  }

  document.addEventListener('onCartFinished', function (e) {
    sync(e && e.detail);
  });

  // Opening the bag from the header icon re-renders the drawer WITHOUT an
  // onCartFinished — the theme fires only cart:section-refreshed. Without this
  // the drawer showed the box ticked (pending) while the fee was never added:
  // a ticked promise the total didn't include (found 22-Oct). Protection only,
  // and loop-safe: once the fee is in the cart the re-rendered card is no
  // longer pending, so a refresh caused by our own add does nothing.
  document.addEventListener('cart:section-refreshed', function () {
    maybeCleanupProtection();
    maybeFixProtectionQty();
    maybeAutoAddProtection(null);
    maybeStampArm();
    maybeStampPromiseArm();
  });

  // The theme writes cart.item_count into the header bag counter, which counts
  // the fee. Correct it after the theme has written (header.liquid does the
  // same on page load).
  function fixHeaderCount(cart) {
    if (!cart || !cart.items) return;
    var extra = 0;
    cart.items.forEach(function (i) {
      if (i.properties && (i.properties._protection === 'true' || i.properties._tote === 'true')) extra += i.quantity;
    });
    if (!extra) return;
    setTimeout(function () {
      var c = document.querySelector('[data-cart-counter]');
      if (!c) return;
      var n = Math.max(0, cart.item_count - extra);
      c.innerHTML = n === 0 ? '' : c.innerHTML.replace(/\d+/, String(n));
    }, 0);
  }
  document.addEventListener('onCartUpdated', function (e) { fixHeaderCount(e && e.detail); });

  /*
   * Checkout guard (review 22-Oct, K3): untick, then tap Checkout inside the
   * write gap, and the checkout was created with the fee still in it. While a
   * cart write is pending, hold the click, wait for the queue (6 s cap), then
   * click the same button again — never form.submit(), which drops
   * name=checkout and strands the customer on /cart.
   */
  var checkoutBypass = false;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!btn || checkoutBypass || pendingWrites <= 0) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    btn.setAttribute('aria-busy', 'true');
    var done = false;
    var go = function () {
      if (done) return; done = true;
      btn.removeAttribute('aria-busy');
      checkoutBypass = true;
      // The removal's bundled re-render REPLACES the cart markup, so the button
      // that was tapped is detached by now and clicking it does nothing (found
      // by RACE1, 22-Oct). Click whichever checkout button is on screen.
      var live = btn.isConnected ? btn : Array.prototype.find.call(
        document.querySelectorAll('button[name="checkout"], input[name="checkout"]'),
        function (b) { return b.offsetParent !== null; });
      (live || btn).click();
      setTimeout(function () { checkoutBypass = false; }, 1000);
    };
    chain.then(go, go);
    setTimeout(go, 6000);
  }, true);

  maybeReset();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sync(null); });
  } else {
    sync(null);
  }

  if (tripped) trip('restored from session');

  window.DOI_CartOffers = {
    version: '2.0.0',
    // Exposed for scripts/verify-mobile.mjs — the test asserts the breaker
    // trips and that no further requests follow.
    _state: function () {
      return { tripped: tripped, failures: failures, lastWriteAt: lastWriteAt };
    }
  };
})();
